import { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin } from "lucide-react";
import { COUNTRY_LIST } from "../lib/countries";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

// Compact autocomplete for hero sections (dark background).
export const CountrySearch = ({ className = "" }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const q = query.trim().toLowerCase();
  const matches = useMemo(
    () => (q ? COUNTRY_LIST.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8) : []),
    [q]
  );

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (slug) => {
    scrollTop();
    navigate(`/visa/${slug}`);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (matches.length > 0) go(matches[0].slug);
  };

  return (
    <div ref={wrapRef} className={`relative w-full max-w-xl ${className}`} data-testid="hero-country-search">
      <form onSubmit={onSubmit} role="search">
        <label htmlFor="hero-country-search-input" className="sr-only">Search a country visa page</label>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#ef233c] pointer-events-none" />
          <input
            id="hero-country-search-input"
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Find your country's visa (e.g. India, Brazil, Kenya)…"
            autoComplete="off"
            className="w-full rounded-full bg-white/10 border border-white/20 pl-12 pr-40 py-3.5 text-[15px] text-white placeholder:text-white/50 backdrop-blur-sm focus:outline-none focus:border-[#ef233c] focus:bg-white/15 transition-colors"
            data-testid="hero-country-search-input"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(""); setOpen(false); }}
              className="absolute right-32 top-1/2 -translate-y-1/2 text-white/50 hover:text-white transition-colors"
              aria-label="Clear search"
              data-testid="hero-country-search-clear"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center gap-2 rounded-full bg-[#d90429] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#ef233c] transition-colors"
            aria-label="Search countries"
            data-testid="hero-country-search-submit"
          >
            <Search className="w-4 h-4" /> Search
          </button>
        </div>
      </form>

      {open && q && (
        <div className="absolute z-30 mt-2 w-full rounded-2xl border border-slate-200 bg-white shadow-[0_20px_60px_rgba(11,25,44,0.25)] overflow-hidden" data-testid="hero-country-search-results">
          {matches.length === 0 ? (
            <div className="px-4 py-4 text-sm text-slate-500">No match for "{query}" — call us and we likely still handle it.</div>
          ) : (
            matches.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => go(c.slug)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#f8f9fa] transition-colors border-b border-slate-100 last:border-0"
                data-testid={`hero-country-result-${c.slug}`}
              >
                <MapPin className="w-4 h-4 text-[#d90429] shrink-0" />
                <span className="text-[15px] font-medium text-[#0b192c]">{c.name}</span>
                <span className="ml-auto text-xs text-slate-400">Visa services</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
